import { Injectable } from '@angular/core';
import {
  convertFromUTC,
  defaultTimezone,
  formatToLocaleDateString,
  SupportedTimezone,
  supportedTimezones,
  toAPIDateTimeStringFormat,
} from '@shared/datetime/datetime.utils';
import { isDefined } from '@stillmandigital/common';
import dayjs from 'dayjs';
import { BehaviorSubject, map, skip } from 'rxjs';

const TIMEZONE_STORAGE_KEY = 'hrn_ls_timezone';

const LEGACY_STORED_TIMEZONES: Record<string, SupportedTimezone> = { CST: 'US/Central' };

function loadStoredTimezone(): SupportedTimezone {
  const raw = localStorage.getItem(TIMEZONE_STORAGE_KEY);
  const stored = raw ? (LEGACY_STORED_TIMEZONES[raw] ?? raw) : raw;
  return stored && (supportedTimezones as string[]).includes(stored) ? (stored as SupportedTimezone) : defaultTimezone;
}

@Injectable({ providedIn: 'root' })
export class DateTimeService {
  private readonly _timezone$ = new BehaviorSubject<SupportedTimezone>(loadStoredTimezone());

  public readonly timezone$ = this._timezone$.asObservable();

  public readonly timezoneChange$ = this.timezone$.pipe(skip(1));

  public get timezone() {
    return this._timezone$.value;
  }

  public setTimezone(value: SupportedTimezone) {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, value);
    this._timezone$.next(value);
  }

  public formatLocaleDate$ = (value: string | number | undefined | Date, format = 'medium') =>
    this.timezone$.pipe(map(timezone => formatToLocaleDateString(value, timezone, format)));

  public formatLocaleDate(value: string | number | undefined | Date, format = 'medium') {
    return formatToLocaleDateString(value, this.timezone, format);
  }

  public toAPIDate(value: Date | dayjs.Dayjs | string | number | undefined): string {
    return toAPIDateTimeStringFormat(value, this.timezone);
  }

  public fromAPIDate(value: string | undefined | Date): string {
    return convertFromUTC(value, this.timezone);
  }

  public toAPIDateOrUndefined(value: Date | dayjs.Dayjs | string | number | undefined): string | undefined {
    return isDefined(value) ? toAPIDateTimeStringFormat(value, this.timezone) : undefined;
  }
}
